import React, { useMemo, useRef, useState } from "react";
import { ReloadOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { Alert, Button, Empty, Modal, Segmented, Space, Spin, theme, Typography } from "antd";
import { useTranslation } from "react-i18next";

import {
  formatPhotoDate,
  pickDefaultPair,
  type ComparePair,
  type ComparePhoto,
} from "@fitness-app/client-shared/features/measurements/compare-photo";
import {
  applyZoom,
  CARD_HEIGHT,
  CARD_WIDTH,
  createDefaultSlot,
  createDefaultState,
  ZOOM_STEP,
  type PhotoCompareMode,
  type PhotoCompareSlotKey,
  type PhotoCompareState,
} from "@fitness-app/client-shared/features/measurements/photo-compare-layout";
import { ClientPhotoType } from "@fitness-app/data-models/entities/Measurement";

import { useHeicImage } from "~/hooks/useHeicImage";
import { useIsPhone } from "~/hooks/useIsMobile";
import { type PhotoCompareTexts } from "./drawPhotoCompare";
import { imageSizes, PhotoCompareCanvas } from "./PhotoCompareCanvas";
import { useAllTraineeBodyPhotos } from "./useAllTraineeBodyPhotos";
import { useCompareImages } from "./useCompareImages";

// TRE-2290 — the trainer's before / after comparison of a client's body photos. Mirrors the
// client-web dialog (TRE-2105): pick a pair of the same view, slider or side by side, drag and
// zoom each photo to line the bodies up. No face covers and no export on the trainer side.

const TYPES = [ClientPhotoType.Front, ClientPhotoType.Back, ClientPhotoType.Side];
const SLOTS: PhotoCompareSlotKey[] = ["before", "after"];
/** Keeps the card and its controls on a laptop screen. */
const MAX_CANVAS_HEIGHT = 560;

const PhotoThumb = ({ photo, style }: { photo: ComparePhoto; style: React.CSSProperties }) => {
  // The `thumb_` derivative is generated after upload and can be missing; fall back to the photo.
  const [thumbFailed, setThumbFailed] = useState(false);
  const { src } = useHeicImage(photo.thumbnailUrl && !thumbFailed ? photo.thumbnailUrl : photo.url);
  return (
    <img
      src={src || undefined}
      alt=""
      className="ph-no-capture"
      style={{ objectFit: "cover", display: "block", ...style }}
      onError={() => setThumbFailed(true)}
    />
  );
};

const SlotButton = ({
  title,
  photo,
  active,
  onClick,
}: {
  title: string;
  photo: ComparePhoto | null;
  active: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation("trainees");
  const { token } = theme.useToken();
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 p-2 text-left"
      style={{
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${active ? token.colorPrimary : token.colorBorder}`,
        background: active ? token.colorPrimaryBg : token.colorBgContainer,
      }}
    >
      {photo ? (
        <PhotoThumb photo={photo} style={{ width: 44, height: 56, borderRadius: token.borderRadius }} />
      ) : (
        <div
          style={{ width: 44, height: 56, borderRadius: token.borderRadius, background: token.colorFillSecondary }}
        />
      )}
      <div className="min-w-0 flex-1">
        <Typography.Text type="secondary" strong className="block text-xs uppercase">
          {title}
        </Typography.Text>
        <Typography.Text strong ellipsis className="block">
          {photo ? formatPhotoDate(photo.eventDate) : t("photos.compare.select")}
        </Typography.Text>
        {photo ? (
          <Typography.Text type="secondary" className="block text-xs">
            {t(`photos.type.${photo.type}`)}
          </Typography.Text>
        ) : null}
      </div>
    </button>
  );
};

const PhotoPicker = ({
  photos,
  selectedPhotoId,
  initialType,
  onSelect,
}: {
  photos: ComparePhoto[];
  selectedPhotoId: string | null;
  initialType: ClientPhotoType;
  onSelect: (photo: ComparePhoto) => void;
}) => {
  const { t } = useTranslation("trainees");
  const { token } = theme.useToken();
  const [type, setType] = useState<ClientPhotoType>(initialType);
  const visible = photos.filter((photo) => photo.type === type);

  return (
    <div
      className="flex flex-col gap-3 p-3"
      style={{ borderRadius: token.borderRadiusLG, background: token.colorFillQuaternary }}
    >
      <Segmented<ClientPhotoType>
        block
        value={type}
        onChange={setType}
        options={TYPES.map((item) => ({ value: item, label: t(`photos.type.${item}`) }))}
      />
      {visible.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("photos.compare.noPhotosOfType")} />
      ) : (
        <div className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
          {visible.map((photo) => {
            const selected = photo.photoId === selectedPhotoId;
            return (
              <button
                key={photo.photoId}
                type="button"
                onClick={() => onSelect(photo)}
                className="relative cursor-pointer overflow-hidden border-0 p-0"
                style={{
                  aspectRatio: "4 / 5",
                  borderRadius: token.borderRadius,
                  background: token.colorFillSecondary,
                  outline: selected ? `2px solid ${token.colorPrimary}` : "none",
                  outlineOffset: 1,
                }}
              >
                <PhotoThumb photo={photo} style={{ width: "100%", height: "100%" }} />
                <span className="absolute bottom-1 left-1 rounded bg-black/55 px-1 text-[10px] text-white">
                  {formatPhotoDate(photo.eventDate)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CompareBody = ({ photos }: { photos: ComparePhoto[] }) => {
  const { t } = useTranslation("trainees");
  const { token } = theme.useToken();
  const [pair, setPair] = useState<ComparePair>(() => pickDefaultPair(photos));
  const [state, setState] = useState<PhotoCompareState>(() => createDefaultState("slider"));
  const [picking, setPicking] = useState<PhotoCompareSlotKey | null>(null);
  /** Photo the zoom buttons act on: the last one touched on the canvas. */
  const [activeSlot, setActiveSlot] = useState<PhotoCompareSlotKey>("after");
  const [canvasWidth, setCanvasWidth] = useState(0);
  const areaObserver = useRef<ResizeObserver | null>(null);
  const { images, loading, failed, retry } = useCompareImages(pair);

  const titles: Record<PhotoCompareSlotKey, string> = {
    before: t("photos.compare.before"),
    after: t("photos.compare.after"),
  };

  const texts = useMemo<PhotoCompareTexts>(
    () => ({
      dates: {
        before: pair.before ? formatPhotoDate(pair.before.eventDate) : "-",
        after: pair.after ? formatPhotoDate(pair.after.eventDate) : "-",
      },
      titles: { before: t("photos.compare.beforeUppercase"), after: t("photos.compare.afterUppercase") },
      choosePhoto: t("photos.compare.choosePhoto"),
    }),
    [pair, t],
  );

  // Callback ref rather than an effect: the modal body mounts through a portal after the first
  // commit, so an effect would measure a node that is not there yet.
  const areaRef = (node: HTMLDivElement | null) => {
    areaObserver.current?.disconnect();
    areaObserver.current = null;
    if (!node) {
      return;
    }
    const measure = () => {
      const byHeight = (MAX_CANVAS_HEIGHT * CARD_WIDTH) / CARD_HEIGHT;
      setCanvasWidth(Math.max(0, Math.min(node.clientWidth, byHeight)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    areaObserver.current = observer;
  };

  const onPickPhoto = (photo: ComparePhoto) => {
    if (!picking) {
      return;
    }
    const slot = picking;
    setPair((current) => ({ ...current, [slot]: photo }));
    setState((current) => ({ ...current, [slot]: createDefaultSlot() }));
    setPicking(null);
  };

  const zoomActive = (factor: number) => {
    setState((current) => applyZoom(current, imageSizes(images), { kind: "photo", slot: activeSlot }, factor));
  };

  const otherSlot: PhotoCompareSlotKey | null = picking ? (picking === "before" ? "after" : "before") : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        {SLOTS.map((slot) => (
          <SlotButton
            key={slot}
            title={titles[slot]}
            photo={pair[slot]}
            active={picking === slot}
            onClick={() => setPicking((current) => (current === slot ? null : slot))}
          />
        ))}
      </div>

      {picking ? (
        <PhotoPicker
          key={picking}
          photos={photos}
          selectedPhotoId={pair[picking]?.photoId ?? null}
          initialType={(otherSlot && pair[otherSlot]?.type) ?? pair[picking]?.type ?? ClientPhotoType.Front}
          onSelect={onPickPhoto}
        />
      ) : null}

      <Segmented<PhotoCompareMode>
        block
        value={state.mode}
        onChange={(mode) => setState((current) => ({ ...current, mode }))}
        options={[
          { value: "slider", label: t("photos.compare.slider") },
          { value: "sideBySide", label: t("photos.compare.sideBySide") },
        ]}
      />

      {failed && !loading ? (
        <Alert
          type="warning"
          showIcon
          message={t("photos.compare.loadFailed")}
          action={
            <Button size="small" onClick={retry}>
              {t("photos.compare.retry")}
            </Button>
          }
        />
      ) : null}

      <div ref={areaRef} className="flex w-full justify-center">
        {canvasWidth > 0 ? (
          <Spin spinning={loading}>
            <PhotoCompareCanvas
              images={images}
              texts={texts}
              state={state}
              onStateChange={setState}
              onSlotTouched={setActiveSlot}
              width={canvasWidth}
              ariaLabel={t("photos.compare.canvasLabel")}
            />
          </Spin>
        ) : null}
      </div>

      <div
        className="flex flex-wrap items-center justify-between gap-3 p-3"
        style={{ borderRadius: token.borderRadiusLG, background: token.colorFillQuaternary }}
      >
        <Space size={8} wrap>
          <Typography.Text strong>{t("photos.compare.zoom")}</Typography.Text>
          {SLOTS.map((slot) => (
            <Button
              key={slot}
              size="small"
              type={activeSlot === slot ? "primary" : "default"}
              onClick={() => setActiveSlot(slot)}
            >
              {titles[slot]}
            </Button>
          ))}
          <Button
            size="small"
            icon={<ZoomOutOutlined />}
            aria-label={t("photos.compare.zoomOut")}
            disabled={!images[activeSlot]}
            onClick={() => zoomActive(1 / ZOOM_STEP)}
          />
          <Button
            size="small"
            icon={<ZoomInOutlined />}
            aria-label={t("photos.compare.zoomIn")}
            disabled={!images[activeSlot]}
            onClick={() => zoomActive(ZOOM_STEP)}
          />
        </Space>
        <Button
          size="small"
          type="text"
          icon={<ReloadOutlined />}
          onClick={() => setState((current) => createDefaultState(current.mode))}
        >
          {t("photos.compare.reset")}
        </Button>
      </div>
    </div>
  );
};

interface TraineeBodyPhotosCompareModalProps {
  traineeId: string;
  onClose: () => void;
}

/** Mounted only while open, so every opening starts from the default pair. */
export const TraineeBodyPhotosCompareModal = ({ traineeId, onClose }: TraineeBodyPhotosCompareModalProps) => {
  const { t } = useTranslation("trainees");
  const isPhone = useIsPhone();
  const { data: photos, isLoading, isError, refetch } = useAllTraineeBodyPhotos(traineeId, true);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-16">
          <Spin />
        </div>
      );
    }
    if (isError || !photos) {
      return (
        <Alert
          type="error"
          showIcon
          message={t("photos.compare.fetchFailed")}
          action={
            <Button size="small" onClick={() => void refetch()}>
              {t("photos.compare.retry")}
            </Button>
          }
        />
      );
    }
    if (photos.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("photos.emptyState")} />;
    }
    return <CompareBody photos={photos} />;
  };

  return (
    <Modal
      open
      title={t("photos.compare.title")}
      onCancel={onClose}
      footer={null}
      width={isPhone ? "100%" : 760}
      style={isPhone ? { top: 8, maxWidth: "calc(100vw - 16px)", paddingBottom: 8 } : { top: 24 }}
    >
      {renderContent()}
    </Modal>
  );
};
